import * as React from 'react';

import { ButtonBase, Typography } from '@mui/material';

import { THUMBNAIL_MAX_HEIGHT } from '@modules/_shared/components/chat/ArtifactThumbnail';
import { ChatImagePreviewDialog } from '@modules/_shared/components/chat/ChatImagePreviewDialog';
import type {
  CrmImage,
  ShownCrmImages,
} from '@modules/_shared/components/chat/crmImages';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

const GRID_THUMBNAIL_HEIGHT = 120;

const ImageTile = ({
  image,
  height,
  onOpen,
}: {
  image: CrmImage;
  height: number;
  onOpen: () => void;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [failed, setFailed] = React.useState(false);
  const box = {
    width: '100%',
    height,
    borderRadius: 6,
    backgroundColor: colors.grey100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  } as const;
  const label = image.title ?? t('Photo');
  return (
    <div css={{ minWidth: 0 }}>
      {failed ? (
        <div css={box} title={label}>
          <Typography
            variant="caption"
            sx={{ color: colors.grey600, px: 1, textAlign: 'center' }}
          >
            {t('Failed to load the preview')}
          </Typography>
        </div>
      ) : (
        <ButtonBase onClick={onOpen} title={label} aria-label={label} sx={box}>
          <img
            src={image.thumbnailUrl}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </ButtonBase>
      )}
      {image.title != null && (
        <Typography
          variant="caption"
          component="div"
          noWrap
          sx={{ color: colors.grey600 }}
        >
          {image.title}
        </Typography>
      )}
    </div>
  );
};

// Card under a finished show_crm_images call: a grid of imgproxy thumbnails
// in display order, each opening the full image in the chat lightbox. The
// images are public CRM storage URLs, so the browser loads them directly.
export const ShowCrmImagesCard = ({ shown }: { shown: ShownCrmImages }) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [open, setOpen] = React.useState<CrmImage | null>(null);
  const single = shown.images.length === 1;
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {shown.title != null && (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {shown.title}
        </Typography>
      )}
      {shown.images.length > 0 && (
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: single
              ? '1fr'
              : 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 6,
          }}
        >
          {shown.images.map(image => (
            <ImageTile
              key={image.id}
              image={image}
              height={single ? THUMBNAIL_MAX_HEIGHT : GRID_THUMBNAIL_HEIGHT}
              onOpen={() => setOpen(image)}
            />
          ))}
        </div>
      )}
      {shown.images.length === 0 && shown.missing.length === 0 && (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {t('No photos to show.')}
        </Typography>
      )}
      {shown.missing.length > 0 && (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {shown.missing.length === 1 && shown.images.length === 0
            ? t('This photo was not found.')
            : t('Some of these photos were not found.')}
        </Typography>
      )}
      {shown.skipped > 0 && (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {t('Some photos cannot be shown here.')}
        </Typography>
      )}
      <ChatImagePreviewDialog
        src={open?.fullUrl ?? null}
        alt={open?.title ?? undefined}
        onClose={() => setOpen(null)}
      />
    </div>
  );
};
